import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormLabel } from '../form-label';
import { FileUploaderSection } from './styled';
import { MultiUploader, SingleUploader /* , UrlUploader */ } from './uploaders';
import { filesValidator, converFileToExtended, updateFilesStatus, genFileUID } from './utils';
const uploadersMap = {
    multi: MultiUploader,
    single: SingleUploader
    /*   url: UrlUploader */
};
export const FileUploader = ({ dropzone, buttonText, noClick, title, fileList, accept, className, name, maxFiles = 1, maxSize, type = 'multi', label, labelTooltip, hint, hintType, caption, disabled = false, required, fixedPadding, customFetch, customFileValidator, onDownload, onAbort, onAttach, onDetach, onPreview }) => {
    const [files, setFiles] = useState(() => fileList || []);
    // TODO: it won't rerender and change type of hint
    /*   const [customHint, setCustomHint] = useState<{
      hintType: MessageInfoTypeT | undefined;
      hint: string;
    }>({
      hintType,
      hint: typeof hint === 'string' ? hint : ''
    }); */
    const uploaderValidator = customFileValidator !== null && customFileValidator !== void 0 ? customFileValidator : filesValidator(files);
    useEffect(() => {
        if (fileList) {
            setFiles(fileList);
        }
    }, [fileList]);
    // provide uid for all files by default if user it didn't
    React.useMemo(() => {
        (fileList || []).forEach((file) => {
            if (!file.uid && !Object.isFrozen(file)) {
                file.uid = genFileUID(file.name);
            }
        });
    }, [fileList]);
    const handleAttach = useCallback(async (filesToAttach) => {
        if (!onAttach && filesToAttach.length === 0)
            return;
        const acceptedFilesNames = filesToAttach.map((file) => file.name);
        try {
            setFiles((prevFiles) => updateFilesStatus('uploading', acceptedFilesNames, prevFiles));
            await (onAttach === null || onAttach === void 0 ? void 0 : onAttach({ files: filesToAttach, allFiles: files }));
            setFiles((prevFiles) => updateFilesStatus('done', acceptedFilesNames, prevFiles));
        }
        catch (error) {
            setFiles((prevFiles) => updateFilesStatus('error', acceptedFilesNames, prevFiles).map((file) => {
                var _a;
                return ({
                    ...file,
                    errors: [{ message: error.message, code: (_a = error === null || error === void 0 ? void 0 : error.code) !== null && _a !== void 0 ? _a : 'network-error' }]
                });
            }));
            throw { message: error.message };
        }
    }, [onAttach]);
    const onDrop = useCallback(async (acceptedFiles, droppedRejectedFiles) => {
        const convertedAccepted = acceptedFiles.map(converFileToExtended);
        const convertedDropped = droppedRejectedFiles.map(({ file, errors }) => ({
            errors,
            ...converFileToExtended(file),
            uploadStatus: 'error'
        }));
        setFiles((prevFiles) => [...prevFiles, ...convertedAccepted, ...convertedDropped]);
        handleAttach(convertedAccepted);
    }, [handleAttach]);
    const handleRemoveFile = async (fileToRemove) => {
        // TODO: 1. replace setting files to utils
        //       2. make uid not required
        //       3. if uid not provided looking files by their name
        setFiles((oldFiles) => [...oldFiles.filter((oldFile) => oldFile.uid !== fileToRemove.uid)]);
        if (onDetach) {
            await onDetach({ fileToRemove, allFiles: files });
        }
    };
    const handleAbort = async (file) => {
        setFiles((oldFiles) => [...oldFiles.filter((oldFile) => oldFile.uid !== file.uid)]);
        if (onAbort) {
            await onAbort(file);
        }
    };
    const handlePreview = (file) => {
        if (!file.originalFile && !file.url) {
            throw new Error('No file selected for preview');
        }
        const previewSrc = file.originalFile ? URL.createObjectURL(file.originalFile) : file.url;
        if (previewSrc) {
            window.open(previewSrc, '_blank', 'noopener,noreferrer');
        }
    };
    const handleDownloadFile = (file) => {
        if (file.originalFile || file.url) {
            const url = file.originalFile ? URL.createObjectURL(file.originalFile) : file.url;
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            a.target = '_blank';
            a.click();
            if (file.originalFile) {
                URL.revokeObjectURL(url);
            }
            document.body.removeChild(a);
            return;
        }
    };
    const { getRootProps, getInputProps, isFocused, isDragActive, isDragAccept, isDragReject } = useDropzone({
        noClick,
        maxFiles,
        maxSize,
        disabled,
        multiple: type === 'multi',
        onDrop,
        accept,
        validator: uploaderValidator
    });
    const UploaderComponent = uploadersMap[type];
    return (React.createElement(FileUploaderSection, { className: className, "data-testid": "file-uploader-container" },
        label && (React.createElement(FormLabel, { htmlFor: name, labelTooltip: labelTooltip, required: required }, label)),
        React.createElement(UploaderComponent, { customFetch: customFetch, buttonText: buttonText, dropzone: dropzone, rootProps: getRootProps({
                isFocused,
                isDragActive,
                isDragAccept,
                isDragReject,
                disabled
            }), inputProps: getInputProps({ id: name }), maxSize: maxSize, title: title, caption: caption, hint: hint, hintType: hintType, fixedPadding: fixedPadding, files: files, onAttach: handleAttach, onPreview: onPreview !== null && onPreview !== void 0 ? onPreview : handlePreview, onDownload: onDownload !== null && onDownload !== void 0 ? onDownload : handleDownloadFile, onRemoveFile: handleRemoveFile, onAbort: handleAbort })));
};
